import React, { useEffect, useState } from 'react';
import { studentAttendanceAPI, masterAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiCheck, FiX, FiClock, FiCalendar, FiUsers, FiSave } from 'react-icons/fi';

const Attendance = () => {
  const [classes, setClasses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [filters, setFilters] = useState({
    date: new Date().toISOString().split('T')[0],
    class_id: '',
    batch_id: ''
  });
  
  const [attendance, setAttendance] = useState({});

  useEffect(() => {
    fetchClasses();
    fetchBatches();
  }, []);

  useEffect(() => {
    if (filters.class_id || filters.batch_id) {
      fetchStudents();
    }
  }, [filters.date, filters.class_id, filters.batch_id]);

  const fetchClasses = async () => {
    try {
      const response = await masterAPI.getClasses();
      if (response.data.success) {
        setClasses(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };

  const fetchBatches = async () => {
    try {
      const response = await masterAPI.getBatches();
      if (response.data.success) {
        setBatches(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching batches:', error);
    }
  };

  const fetchStudents = async () => {
    if (!filters.class_id && !filters.batch_id) {
      setStudents([]);
      return;
    }

    setLoading(true);
    try {
      const response = await studentAttendanceAPI.getStudentsForAttendance(filters);
      if (response.data.success) {
        const studentsData = response.data.data;
        setStudents(studentsData);
        
        // Initialize attendance state with existing data
        const attendanceData = {};
        studentsData.forEach(student => {
          attendanceData[student.id] = {
            status: student.status || 'present',
            remarks: student.remarks || ''
          };
        });
        setAttendance(attendanceData);
      }
    } catch (error) {
      toast.error('Error fetching students');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleStatusChange = (studentId, status) => {
    setAttendance(prev => ({
      ...prev,
      [studentId]: { ...prev[studentId], status }
    }));
  };

  const handleRemarksChange = (studentId, remarks) => {
    setAttendance(prev => ({
      ...prev,
      [studentId]: { ...prev[studentId], remarks }
    }));
  };

  const markAllAs = (status) => {
    const newAttendance = {};
    students.forEach(student => {
      newAttendance[student.id] = {
        status,
        remarks: attendance[student.id]?.remarks || ''
      };
    });
    setAttendance(newAttendance);
    toast.success(`Marked all students as ${status}`);
  };

  const handleSubmit = async () => {
    if (students.length === 0) {
      toast.error('No students to mark attendance for');
      return;
    }

    if (!window.confirm(`Save attendance for ${students.length} student(s) on ${filters.date}?`)) {
      return;
    }

    setSubmitting(true);
    try {
      const attendanceData = students.map(student => ({
        student_id: student.id,
        status: attendance[student.id]?.status || 'present',
        remarks: attendance[student.id]?.remarks || null
      }));

      const response = await studentAttendanceAPI.markAttendance({
        date: filters.date,
        attendance: attendanceData
      });

      if (response.data.success) {
        toast.success(response.data.message);
        fetchStudents(); // Refresh to show updated data
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error marking attendance');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'present': return 'bg-green-100 text-green-800 border-green-300';
      case 'absent': return 'bg-red-100 text-red-800 border-red-300';
      case 'late': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'half_day': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'leave': return 'bg-purple-100 text-purple-800 border-purple-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'present': return <FiCheck />;
      case 'absent': return <FiX />;
      case 'late': return <FiClock />;
      default: return null;
    }
  };

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Mark Attendance</h1>
          <p className="text-sm md:text-base text-gray-600 mt-1">Record daily student attendance</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FiCalendar className="inline mr-1" /> Date *
            </label>
            <input
              type="date"
              name="date"
              value={filters.date}
              onChange={handleFilterChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Class
            </label>
            <select
              name="class_id"
              value={filters.class_id}
              onChange={handleFilterChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent"
            >
              <option value="">All Classes</option>
              {classes.map(cls => (
                <option key={cls.id} value={cls.id}>{cls.class}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Batch/Section
            </label>
            <select
              name="batch_id"
              value={filters.batch_id}
              onChange={handleFilterChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent"
            >
              <option value="">All Batches</option>
              {batches.map(batch => (
                <option key={batch.id} value={batch.id}>{batch.batch}</option>
              ))}
            </select>
          </div>
        </div>

        {students.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-200">
            <button
              onClick={() => markAllAs('present')}
              className="px-3 py-1.5 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm font-medium"
            >
              <FiCheck className="inline mr-1" /> Mark All Present
            </button>
            <button
              onClick={() => markAllAs('absent')}
              className="px-3 py-1.5 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm font-medium"
            >
              <FiX className="inline mr-1" /> Mark All Absent
            </button>
            <button
              onClick={() => markAllAs('late')}
              className="px-3 py-1.5 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition-colors text-sm font-medium"
            >
              <FiClock className="inline mr-1" /> Mark All Late
            </button>
          </div>
        )}
      </div>

      {/* Students List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading students...</p>
        </div>
      ) : students.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <FiUsers size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Students Found</h3>
          <p className="text-gray-600">
            {!filters.class_id && !filters.batch_id
              ? 'Please select a class or batch to view students'
              : 'No students found for the selected filters'}
          </p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Roll No</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Student Name</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Class</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Batch</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Remarks</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {students.map((student) => (
                    <tr key={student.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">{student.roll_no || '-'}</td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {student.firstname} {student.lastname}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{student.class_name || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{student.batch_name || '-'}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          {['present', 'absent', 'late', 'half_day', 'leave'].map(status => (
                            <button
                              key={status}
                              onClick={() => handleStatusChange(student.id, status)}
                              className={`px-2 py-1 text-xs font-medium rounded border transition-colors ${
                                attendance[student.id]?.status === status
                                  ? getStatusColor(status)
                                  : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                              }`}
                              title={status.replace('_', ' ').toUpperCase()}
                            >
                              {getStatusIcon(status)}
                              <span className="ml-1 hidden sm:inline">{status.replace('_', ' ')}</span>
                            </button>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="text"
                          value={attendance[student.id]?.remarks || ''}
                          onChange={(e) => handleRemarksChange(student.id, e.target.value)}
                          placeholder="Optional notes..."
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-600 focus:border-transparent"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              <FiSave /> {submitting ? 'Saving...' : 'Save Attendance'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default Attendance;
